/* global gon */
import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { ActionCableProvider, ActionCableConsumer } from 'react-actioncable-provider';
import { getConversation, getConversations } from '../../../api/conversations';
import { isPhoneCallsEnabled } from '../../../helpers/feature_checker.js';
import { useFeatureCallout } from '../../overlays/feature_callout_overlay/use_feature_callout';
import { MENTOR_VIDEO_URL } from '../../phone_calls/phone_calls_guide/mentor_guide';
import { PhosphorIcon, PHOSPHOR_PATHS } from './phosphor_icons';

import ActionCable from 'actioncable';
import Context from '../../../utils/context';
import ConversationOtherUserProfile from './profile_panel/conversation_other_user_profile';
import Conversation from './conversation';
import VoiceCallsWelcomeModal from '../../overlays/voice_calls_welcome_modal';
import Modal from '../../overlays/modal';
import ConversationList from './conversation_list';
import LoadingScreen from '../../system/loading_screen';
import ConversationRoomLoadingSkeleton from './loading_screens/conversation_room_loading_skeleton';
import PostConversationModal from '../../questionnaire/post_conversation_survey_modal/index_gated.jsx';
import Pagination from '../../system/pagination';

import styles from './unified_layout.module.css';

function SidebarChevronDown({ className }) {
  return (
    <svg
      className={className}
      width='18'
      height='18'
      viewBox='0 0 18 18'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      aria-hidden='true'
    >
      <path
        d='M4.5 6.75L9 11.25L13.5 6.75'
        stroke='rgba(255, 255, 255, 0.4)'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
}

export default function ConversationRoom({ match }) {
  let conversation_id = match.params.conversation_id;
  let history = useHistory();
  const context = useContext(Context);
  const user = context.auth.getCurrentUser();
  const currentAccount = context.auth.currentAccount();
  const viewport = context.viewport;
  const isMobile = viewport.getIsMobile();
  const actionCableUrl = gon.action_cable_url;
  const cable = useMemo(() => ActionCable.createConsumer(actionCableUrl), [actionCableUrl]);
  const [conversations, setConversations] = useState(null);
  const [conversation, setConversation] = useState(null);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [surveyEligible, setSurveyEligible] = useState(false);
  const [showSurveyBanner, setShowSurveyBanner] = useState(false);

  // The survey banner cannot be dismissed: it stays visible for the whole
  // session while the mentee is eligible and only disappears once the survey is
  // completed (which turns eligibility off). The recurring survey modal is a
  // separate nudge handled below via useFeatureCallout.
  const handleStartSurvey = () => {
    if (conversation?.post_survey_path) {
      history.push(`${conversation.post_survey_path}?source=banner`);
    }
  };

  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [conversationListLoading, setConversationListLoading] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [sidebarAvatarFailed, setSidebarAvatarFailed] = useState(false);
  const [mobileProfileSheetOpen, setMobileProfileSheetOpen] = useState(false);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(
    user?.current_profile?.unread_notification_count || 0
  );
  const profileMenuRef = useRef(null);
  const conversationListRequestRef = useRef(0);
  // react-actioncable-provider captures onReceived only once at mount and never
  // re-subscribes when the channel prop changes, so we route received events
  // through refs that point at the latest handlers. Without this the handler
  // keeps calling a stale fetchConversation/updateRoom from an earlier render
  // (often closing over an outdated conversation_id), which is why things like
  // the translation refresh broadcast appear to "stop working" after navigating
  // between conversations until a hard refresh.
  const updateRoomRef = useRef(null);
  const updateProfileEventsRef = useRef(null);

  let numConversationsWithUnreadMessages = 0;
  if (conversations) {
    numConversationsWithUnreadMessages = conversations.filter((x) => x.unread_messages > 0).length;
  }

  useEffect(() => {
    if (user.show_welcome_modal) {
      context.auth.userUpdate({ show_welcome_modal: false });
      setShowWelcomeModal(true);
    }
  }, []);

  useEffect(() => {
    return () => {
      cable.disconnect();
    };
  }, [cable]);

  useEffect(() => {
    if (!profileMenuOpen) return undefined;

    const closeProfileMenu = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setProfileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', closeProfileMenu);
    return () => document.removeEventListener('mousedown', closeProfileMenu);
  }, [profileMenuOpen]);

  useEffect(() => {
    if (!isMobile || !window.visualViewport) return;

    let rafId = null;
    let lastHeight = null;

    const updateViewportHeight = () => {
      rafId = null;
      const newHeight = window.visualViewport.height;
      if (newHeight !== lastHeight) {
        lastHeight = newHeight;
        document.documentElement.style.setProperty('--mobile-viewport-height', `${newHeight}px`);
      }
    };

    const handleViewportChange = () => {
      if (rafId === null) {
        rafId = window.requestAnimationFrame(updateViewportHeight);
      }
    };

    updateViewportHeight();
    window.visualViewport.addEventListener('resize', handleViewportChange);
    window.visualViewport.addEventListener('scroll', handleViewportChange);

    return () => {
      window.visualViewport.removeEventListener('resize', handleViewportChange);
      window.visualViewport.removeEventListener('scroll', handleViewportChange);
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
      document.documentElement.style.removeProperty('--mobile-viewport-height');
    };
  }, [isMobile]);

  const fetchAllConversations = (page = 1, { showListLoader = false } = {}) => {
    const requestId = conversationListRequestRef.current + 1;
    conversationListRequestRef.current = requestId;

    if (showListLoader) {
      setConversationListLoading(true);
    }

    getConversations(
      (conversations, meta) => {
        if (requestId !== conversationListRequestRef.current) {
          return;
        }

        // Filter out conversations without messages for PatientPartner users
        if (user.current_profile_type === 'PatientPartner') {
          conversations = conversations.filter((c) => c.messages.length > 0);
        }

        // Backend now handles sorting by last message activity
        setConversations(conversations);
        setPagination(meta.pagination);
        setCurrentPage(meta.pagination?.current_page || page);
        setConversationListLoading(false);
      },
      page,
      () => {
        if (requestId !== conversationListRequestRef.current) {
          return;
        }

        setConversationListLoading(false);
      }
    );
  };

  const handlePageChange = (page) => {
    if (conversationListLoading || page === pagination?.current_page) {
      return;
    }

    fetchAllConversations(page, { showListLoader: true });
  };

  const fetchConversation = (showLoad) => {
    if (conversation_id) {
      if (showLoad) {
        setLoading(true);
      }
      getConversation(
        conversation_id,
        (conversation) => {
          setConversation(conversation);
          scrollToBottomOfChat();
          setLoading(false);

          const eligibleForSurvey =
            user.current_profile_type === 'Patient' &&
            conversation.conversation_survey &&
            conversation.post_survey_path &&
            conversation.patient_mentor_connection_completed &&
            !conversation.post_survey_completed &&
            conversation.post_survey_completed !== 'not_applicable';

          setSurveyEligible(Boolean(eligibleForSurvey));
          setShowSurveyBanner(Boolean(eligibleForSurvey));
        },

        () => {
          setConversation(null);
          setLoading(false);
          history.push('/member/conversations');
        }
      );
    }
  };

  useEffect(() => {
    fetchAllConversations(currentPage);
    fetchConversation(true);
    setMobileProfileSheetOpen(false);
  }, [conversation_id]);

  const scrollToBottomOfChat = () => {
    window.requestAnimationFrame(() => {
      const messageBody = document.querySelector('[data-message-scroll]');
      if (messageBody) {
        messageBody.scrollTop = messageBody.scrollHeight - messageBody.clientHeight;
      }
    });
  };

  const updateRoom = (data) => {
    switch (data.type) {
      case 'message':
        fetchConversation(false);
        if (!conversationListLoading) {
          fetchAllConversations(currentPage);
        }
        break;
      case 'call_update':
        if (!conversation_id || data.conversation_uuid === conversation_id) {
          fetchConversation(false);
        }
        break;
      case 'translation':
        fetchConversation(false);
        break;
      default:
        return;
    }
  };

  const updateProfileEvents = (data) => {
    if (data.type !== 'unread_notification_count') {
      return;
    }

    context.auth.setUnreadNotificationCount(data.value);
    setUnreadNotificationCount(data.value);
  };

  updateRoomRef.current = updateRoom;
  updateProfileEventsRef.current = updateProfileEvents;

  const subscribedConversationIds = useMemo(() => {
    const ids = new Set();
    if (conversations) {
      conversations.forEach((c) => {
        if (c?.uuid) ids.add(c.uuid);
      });
    }
    if (conversation_id) ids.add(conversation_id);
    return Array.from(ids);
  }, [conversations, conversation_id]);

  const subscribedConversationsKey = useMemo(
    () => subscribedConversationIds.slice().sort().join(','),
    [subscribedConversationIds]
  );

  const handleUnreadNotificationCountChange = (value) => {
    context.auth.setUnreadNotificationCount(value);
    setUnreadNotificationCount(value);
  };

  const renderPagination = () =>
    pagination?.total_pages > 1 ? (
      <Pagination pagination={pagination} onPageChange={handlePageChange} loading={conversationListLoading} />
    ) : null;

  // Post-conversation survey modal. Dismissing it snoozes the modal for 24
  // hours (the snooze timestamp is persisted server-side via seen_callouts, so
  // it survives reloads and other devices); after the window elapses the modal
  // re-surfaces on the mentee's next session, and this repeats indefinitely
  // until the survey is completed. Completing the survey flips `surveyEligible`
  // off, which permanently stops the modal.
  const SURVEY_MODAL_RESHOW_MS = 24 * 60 * 60 * 1000;
  const surveyModalBlocked = showWelcomeModal || mobileProfileSheetOpen;
  const { shouldShow: shouldShowSurveyModal, dismiss: dismissSurveyModal } = useFeatureCallout({
    calloutKey: 'post_conversation_survey_modal',
    context,
    eligible: surveyEligible && Boolean(conversation?.post_survey_push_due),
    blocked: surveyModalBlocked,
    reshowAfterMs: SURVEY_MODAL_RESHOW_MS,
  });

  // Recurring callout that highlights the phone-call feature for newly
  // matched patients. Dismissing it only snoozes it for CALLS_CALLOUT_RESHOW_MS
  // (see below); it keeps coming back until the patient completes their first
  // call in this conversation, at which point eligibility turns off
  // permanently. It also stays hidden while a call is in flight so we never
  // prompt for a call on top of an existing request.
  const callsCalloutEligible = (() => {
    if (!user || !conversation) return false;
    if (!isPhoneCallsEnabled(currentAccount)) return false;
    if (user.current_profile_type !== 'Patient') return false;
    const otherUserProfile = conversation.other_user_conversations?.[0]?.profile;
    if (!otherUserProfile?.accepts_calls) return false;
    const calls = conversation.calls || [];
    if (calls.some((call) => call.status === 'completed')) return false;
    if (calls.some((call) => ['pending', 'accepted', 'in_progress'].includes(call.status))) return false;
    return true;
  })();

  const callsCalloutBlocked = (() => {
    if (showWelcomeModal || shouldShowSurveyModal || mobileProfileSheetOpen) return true;
    if (user && !user.accepted_privacy) return true;
    if (user && user.current_profile_type === 'Patient' && !user.matched_with_mentor) return true;
    if (conversation?.compliance_acknowledgment_required && !conversation?.current_user_compliance_acknowledged_at) {
      return true;
    }
    return false;
  })();

  const CALLS_CALLOUT_RESHOW_MS = 20 * 60 * 1000;

  const { shouldShow: shouldShowCallsCallout, dismiss: dismissCallsCallout } = useFeatureCallout({
    calloutKey: 'phone_feature',
    context,
    eligible: callsCalloutEligible,
    blocked: callsCalloutBlocked,
    reshowAfterMs: CALLS_CALLOUT_RESHOW_MS,
  });

  // One-time welcome modal that introduces voice calls to mentors. Unlike the
  // patient phone-feature callout, this appears once on login (no conversation
  // required) whenever phone calls are available for the account and the
  // mentor accepts calls.
  const mentorVoiceCallsWelcomeEligible = (() => {
    if (!user) return false;
    if (user.current_profile_type !== 'PatientPartner') return false;
    if (!isPhoneCallsEnabled(currentAccount)) return false;
    if (!user.current_profile?.accepts_calls) return false;
    return true;
  })();

  const mentorVoiceCallsWelcomeBlocked = (() => {
    if (shouldShowSurveyModal || mobileProfileSheetOpen) return true;
    if (user && !user.accepted_privacy) return true;
    return false;
  })();

  const { shouldShow: shouldShowMentorVoiceCallsWelcome, dismiss: dismissMentorVoiceCallsWelcome } = useFeatureCallout({
    calloutKey: 'mentor_voice_calls_welcome',
    context,
    eligible: mentorVoiceCallsWelcomeEligible,
    blocked: mentorVoiceCallsWelcomeBlocked,
  });

  const handleSeeHowVoiceCallsWork = () => {
    dismissMentorVoiceCallsWelcome();
    history.push('/member/phone_calls/mentor');
  };

  // Render left panel (conversation list)
  const renderLeftPanel = () => (
    <div className={styles.scrollable_content}>
      <ConversationList
        conversations={conversations}
        pathToMatch={'/member/conversations/'}
        selectedConversation={match.params.conversation_id}
        unreadNotificationCount={unreadNotificationCount}
        onUnreadNotificationCountChange={handleUnreadNotificationCountChange}
        isLoading={conversationListLoading}
        footer={renderPagination()}
      />
    </div>
  );

  const userDisplayName =
    user?.profile?.display_name ||
    user?.display_name ||
    user?.name ||
    [user?.first_name, user?.last_name].filter(Boolean).join(' ') ||
    'PatientPartner';
  const userInitials = userDisplayName
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
  const isMentor = user?.current_profile_type === 'PatientPartner';
  const phoneCallsEnabled = isMentor && isPhoneCallsEnabled(context.auth.currentAccount());
  const showRightPanel = !!conversation;
  const userAvatarUrl = user?.profile?.photo || user?.current_profile?.photo || '';
  const handleLogout = () => {
    context.auth.userLogOut();
  };

  const renderAppSidebar = () => (
    <aside className={styles.app_sidebar} aria-label='Messages navigation'>
      <Link to='/member/conversations' className={styles.app_sidebar_logo}>
        <img src='/wordmark.svg' alt='PatientPartner' draggable='false' />
      </Link>

      <nav className={styles.app_sidebar_nav}>
        <p className={styles.app_sidebar_eyebrow}>Menu</p>
        <Link to='/member/conversations' className={`${styles.app_sidebar_item} ${styles.app_sidebar_item_active}`}>
          <span className={styles.app_sidebar_icon}>
            <PhosphorIcon path={PHOSPHOR_PATHS.chatCircle.fill} size={18} />
            {numConversationsWithUnreadMessages > 0 && <i />}
          </span>
          <span>Messages</span>
        </Link>
        {phoneCallsEnabled && (
          <Link to='/member/calls' className={styles.app_sidebar_item}>
            <span className={styles.app_sidebar_icon}>
              <PhosphorIcon path={PHOSPHOR_PATHS.phone.regular} size={18} />
            </span>
            <span>Calls</span>
            <small>New</small>
          </Link>
        )}
      </nav>

      <div className={styles.app_sidebar_footer}>
        <div className={styles.app_sidebar_profile_area} ref={profileMenuRef}>
          {profileMenuOpen && (
            <div className={styles.app_sidebar_profile_menu}>
              <Link to='/member/settings' onClick={() => setProfileMenuOpen(false)}>
                <PhosphorIcon path={PHOSPHOR_PATHS.gear.regular} size={16} />
                <span>Settings</span>
              </Link>
              <button type='button' onClick={handleLogout}>
                <PhosphorIcon path={PHOSPHOR_PATHS.signOut.regular} size={16} />
                <span>Sign out</span>
              </button>
            </div>
          )}

          <button
            type='button'
            className={`${styles.app_sidebar_profile} ${profileMenuOpen ? styles.app_sidebar_profile_open : ''}`}
            onClick={() => setProfileMenuOpen((open) => !open)}
            aria-expanded={profileMenuOpen}
            aria-haspopup='menu'
          >
            <span className={styles.app_sidebar_avatar}>
              {userAvatarUrl && !sidebarAvatarFailed ? (
                <img src={userAvatarUrl} alt='' draggable='false' onError={() => setSidebarAvatarFailed(true)} />
              ) : (
                userInitials || 'PP'
              )}
            </span>
            <span>
              <strong>{userDisplayName}</strong>
              <small>{isMentor ? 'Mentor' : 'Mentee'}</small>
            </span>
            <SidebarChevronDown className={styles.app_sidebar_profile_caret} />
          </button>
        </div>
      </div>
    </aside>
  );

  // Render middle panel (conversation or splash)
  const renderMiddlePanel = () => {
    if (loading) {
      return (
        <div className={styles.loading_panel}>
          <ConversationRoomLoadingSkeleton />
        </div>
      );
    }

    if (!conversation) {
      return (
        <div className={styles.splash_content}>
          <div className={styles.splash_illustration} aria-hidden='true'>
            <div className={styles.splash_illustration_primary} />
            <div className={styles.splash_illustration_secondary} />
            <span className={styles.splash_illustration_heart}>&#9825;</span>
          </div>
          <div className={styles.splash_copy}>
            <h2>Select a conversation</h2>
            <p>Choose a conversation from the list to view messages and continue the discussion.</p>
          </div>
        </div>
      );
    }

    return (
      <Conversation
        conversation={conversation}
        user={user}
        fetchConversation={fetchConversation}
        otherUser={conversation.other_user_conversations[0]}
        backPath={isMobile ? '/member/conversations' : undefined}
        profilePath={undefined}
        onProfileOpen={isMobile ? () => setMobileProfileSheetOpen(true) : undefined}
        showCallsCallout={shouldShowCallsCallout}
        onDismissCallsCallout={dismissCallsCallout}
        showSurveyBanner={showSurveyBanner && surveyEligible && !shouldShowSurveyModal}
        surveyMentorName={conversation.patient_mentor_name?.trim()?.split(/\s+/)?.[0]}
        onStartSurvey={handleStartSurvey}
      />
    );
  };

  // Render right panel (profile)
  const renderRightPanel = () => {
    if (!conversation) {
      return null;
    }

    return (
      <div className={styles.scrollable_content}>
        <ConversationOtherUserProfile
          otherUser={conversation.other_user_conversations[0]}
          conversation={conversation}
          onCallCreated={() => fetchConversation(false)}
        />
      </div>
    );
  };

  const renderMobileBottomNav = () => (
    <nav className={styles.mobile_bottom_nav} aria-label='Messages navigation'>
      <Link
        to='/member/conversations'
        className={`${styles.mobile_bottom_nav_item} ${styles.mobile_bottom_nav_item_active}`}
      >
        <span className={styles.mobile_bottom_nav_pill}>
          <span className={styles.mobile_bottom_nav_icon}>
            <PhosphorIcon path={PHOSPHOR_PATHS.chatCircle.fill} size={24} />
          </span>
          <strong>Messages</strong>
        </span>
      </Link>
      {phoneCallsEnabled && (
        <Link to='/member/calls' className={styles.mobile_bottom_nav_item}>
          <span className={styles.mobile_bottom_nav_pill}>
            <span className={styles.mobile_bottom_nav_icon}>
              <PhosphorIcon path={PHOSPHOR_PATHS.phone.regular} size={24} />
            </span>
            <strong>Calls</strong>
          </span>
        </Link>
      )}
      <Link to='/member/settings' className={styles.mobile_bottom_nav_item}>
        <span className={styles.mobile_bottom_nav_pill}>
          <span className={styles.mobile_bottom_nav_icon}>
            <PhosphorIcon path={PHOSPHOR_PATHS.gear.regular} size={24} />
          </span>
          <strong>Settings</strong>
        </span>
      </Link>
    </nav>
  );

  const renderMobileListView = () => (
    <>
      <div className={styles.mobile_list_panel}>
        {conversations === null ? (
          <div className={styles.loading_panel}>
            <LoadingScreen />
          </div>
        ) : (
          <ConversationList
            conversations={conversations}
            pathToMatch={'/member/conversations/'}
            unreadNotificationCount={unreadNotificationCount}
            onUnreadNotificationCountChange={handleUnreadNotificationCountChange}
            isLoading={conversationListLoading}
            footer={renderPagination()}
          />
        )}
      </div>
      {renderMobileBottomNav()}
    </>
  );

  return (
    <ActionCableProvider cable={cable}>
      {subscribedConversationIds.length > 0 && (
        <ActionCableConsumer
          key={subscribedConversationsKey}
          channel={{ channel: 'ConversationsChannel', ids: subscribedConversationIds }}
          onReceived={(data) => updateRoomRef.current?.(data)}
        />
      )}
      {user?.profile_id && (
        <ActionCableConsumer
          channel={{ channel: 'ProfileChannel', id: user.profile_id }}
          onReceived={(data) => updateProfileEventsRef.current?.(data)}
        />
      )}

      <div
        className={`${styles.conversation_room_container} ${
          isMobile && conversation_id ? styles.conversation_room_fixed : ''
        }`}
      >
        <div className={styles.conversation_room_wrapper}>
          {/* Desktop: Three panel layout */}
          {!isMobile && (
            <>
              <div className={`${styles.desktop_panels} ${!showRightPanel ? styles.desktop_panels_unselected : ''}`}>
                {renderAppSidebar()}

                {/* Left Panel - Conversation List */}
                <div className={styles.panel_left}>
                  {conversations === null ? (
                    <div className={styles.loading_panel}>
                      <LoadingScreen />
                    </div>
                  ) : (
                    renderLeftPanel()
                  )}
                </div>

                {/* Middle Panel - Conversation */}
                <div className={styles.panel_middle}>{renderMiddlePanel()}</div>

                {/* Right Panel - Profile */}
                {showRightPanel && <div className={styles.panel_right}>{renderRightPanel()}</div>}
              </div>
            </>
          )}

          {/* Mobile: reference app navigation */}
          {isMobile && (
            <div
              className={`${styles.mobile_container} ${
                conversation_id ? styles.mobile_container_chat : styles.mobile_container_list
              }`}
            >
              {conversation_id ? (
                <div className={styles.mobile_chat_panel}>{renderMiddlePanel()}</div>
              ) : (
                renderMobileListView()
              )}
            </div>
          )}
        </div>

        {/* Modals */}
        {showWelcomeModal && (
          <Modal
            className='welcome-message-modal pp-modal'
            content={
              <>
                <h1 className={styles.welcome_modal_title}>Welcome to PatientPartner</h1>
                <p className={styles.welcome_modal_copy}>
                  <b>PatientPartner mentors are real people and volunteers</b> here to share their personal experience
                  and answer questions you might have about being a patient.
                </p>
                <h5 className={styles.welcome_modal_kicker}>WHAT'S NEXT</h5>
                <ol>
                  <li>
                    <p className={styles.welcome_modal_step_title}>Send your first message</p>
                    <p className={styles.welcome_modal_step_copy}>
                      Introduce yourself to your mentor and ask your first question!
                    </p>
                  </li>
                  <li>
                    <p className={styles.welcome_modal_step_title}>Replies from real people</p>
                    <p className={styles.welcome_modal_step_copy}>Mentors typically reply within 24 hours.</p>
                  </li>
                  <li>
                    <p className={styles.welcome_modal_step_title}>SMS Notification</p>
                    <p className={styles.welcome_modal_step_copy}>
                      We'll send you a text notification letting you know when your mentor replies.
                    </p>
                  </li>
                </ol>
                <button onClick={() => setShowWelcomeModal(false)}>Get Started</button>
              </>
            }
            onClose={() => {
              setShowWelcomeModal(false);
            }}
          />
        )}

        {shouldShowMentorVoiceCallsWelcome && (
          <VoiceCallsWelcomeModal
            videoUrl={MENTOR_VIDEO_URL}
            onDismiss={dismissMentorVoiceCallsWelcome}
            onSeeHow={handleSeeHowVoiceCallsWork}
          />
        )}

        {shouldShowSurveyModal && conversation && (
          <PostConversationModal
            postConversationPath={`${conversation.post_survey_path}?source=in_app_modal`}
            mentorName={conversation.patient_mentor_name?.trim()?.split(/\s+/)?.[0]}
            onDismiss={dismissSurveyModal}
            onClose={dismissSurveyModal}
          />
        )}

        {isMobile && mobileProfileSheetOpen && conversation && (
          <div
            className={styles.mobile_profile_sheet_backdrop}
            role='dialog'
            aria-modal='true'
            aria-label='Conversation member profile'
            onClick={(event) => {
              if (event.target === event.currentTarget) setMobileProfileSheetOpen(false);
            }}
          >
            <div className={styles.mobile_profile_sheet}>
              <div className={styles.mobile_profile_sheet_header}>
                <span className={styles.mobile_profile_sheet_handle} aria-hidden='true' />
                <button
                  type='button'
                  className={styles.mobile_profile_sheet_close}
                  onClick={() => setMobileProfileSheetOpen(false)}
                  aria-label='Close profile'
                >
                  <svg width='16' height='16' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'>
                    <path d='M2 2L10 10M10 2L2 10' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' />
                  </svg>
                </button>
              </div>
              <div className={styles.mobile_profile_sheet_body}>
                <ConversationOtherUserProfile
                  otherUser={conversation.other_user_conversations[0]}
                  conversation={conversation}
                  onCallCreated={() => fetchConversation(false)}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </ActionCableProvider>
  );
}
